import {useEffect, useState} from 'react';

type ItemProps<ItemValue> = {
    value: ItemValue,
    disabled?: boolean,
};

function isNil(value: unknown): value is null | undefined {
    return value == null;
}

type Props<Value> = {
    multiselectable?: boolean,
    value?: Value,
    onChange: ((value: Value, name: string) => void) | undefined,
    name?: string,
    setShown: (shown: boolean) => void,
};

export const useComboboxValue = <ItemValue, Value extends ItemValue | ItemValue[]>({
    multiselectable,
    value: valueProp,
    onChange: onChangeProp,
    name = '',
    setShown,
}: Props<Value>) => {

    function mapValueFromProps() {
        if (multiselectable) {
            return (valueProp ?? []) as ItemValue[];
        } else {
            return isNil(valueProp) ? [] : ([valueProp] as ItemValue[]);
        }
    }

    const [value, setInternalValue] = useState<ItemValue[]>(mapValueFromProps);

    useEffect(() => {
        setInternalValue(mapValueFromProps());
    }, [valueProp, multiselectable]); // eslint-disable-line react-hooks/exhaustive-deps

    const onChange = (itemProps: ItemProps<ItemValue>) => {
        if (itemProps.disabled) {
            return;
        }

        if (multiselectable) {
            const newValue: ItemValue[] = [];
            newValue.push(...value);
            const index = newValue.indexOf(itemProps.value);
            if (index === -1) {
                newValue.push(itemProps.value);
            } else {
                newValue.splice(index, 1);
            }
            setInternalValue(newValue);
            onChangeProp?.(newValue as Value, name);
        } else {
            if (!Object.is(value[0], itemProps.value)) {
                setInternalValue([itemProps.value]);
                onChangeProp?.(itemProps.value as Value, name);
            }
            setShown(false);
        }
    };

    return {
        value,
        onChange,
    };
};
